import Phaser from 'phaser';
import {
  COLOR_CSS,
  DURATIONS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  SAFE_AREA,
} from '../config';
import { Button } from '../ui/Button';
import { audioSystem } from '../systems/AudioSystem';
import { logSceneEvent } from '../utils/automation';
import { parseSliceRouteOptions } from '../utils/sliceMode';

/** The title is the fantasy sell: commandos, breach, swarm, one decisive action. */
export class TitleScene extends Phaser.Scene {
  private playButton!: Button;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    logSceneEvent('TitleScene');
    const routeOptions = parseSliceRouteOptions(window.location.search);
    if (routeOptions.useSliceMode && routeOptions.autostart) {
      this.scene.start('VerticalSliceScene', {
        level: 1,
        tier: 1,
        runMode: routeOptions.runMode,
      });
      return;
    }

    this.cameras.main.fadeIn(DURATIONS.screenTransition, 0, 0, 0);
    audioSystem.playMusic('menu');

    this.createBattleBackdrop();
    this.createBrandLockup();
    this.createActions();

    this.input.keyboard?.on('keydown-ENTER', () => this.onPlayClick());
    this.input.keyboard?.on('keydown-SPACE', () => this.onPlayClick());
  }

  private createBattleBackdrop(): void {
    const art = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'title-battle-bg');
    const scale = Math.max(GAME_WIDTH / art.width, GAME_HEIGHT / art.height);
    art.setScale(scale * 1.015).setDepth(-20);

    this.tweens.add({
      targets: art,
      scaleX: scale * 1.045,
      scaleY: scale * 1.045,
      duration: 9000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const shade = this.add.graphics().setDepth(-10);
    shade.fillStyle(0x02070C, 0.32);
    shade.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Layered bands reserve calm contrast for the mark and controls.
    for (let i = 0; i < 8; i++) {
      const alpha = 0.12 - i * 0.011;
      shade.fillStyle(0x02070C, Math.max(0.02, alpha));
      shade.fillRect(0, 270 + i * 32, GAME_WIDTH, 38);
    }
    shade.fillStyle(0x02070C, 0.5);
    shade.fillRect(0, GAME_HEIGHT - 100, GAME_WIDTH, 100);

    const rails = this.add.graphics().setDepth(-5);
    rails.lineStyle(2, 0x39FF14, 0.42);
    rails.lineBetween(14, SAFE_AREA.top + 10, 14, GAME_HEIGHT - SAFE_AREA.bottom - 8);
    rails.lineBetween(GAME_WIDTH - 14, SAFE_AREA.top + 10, GAME_WIDTH - 14, GAME_HEIGHT - SAFE_AREA.bottom - 8);
    rails.lineStyle(1, 0x00D4FF, 0.35);
    rails.lineBetween(22, SAFE_AREA.top + 18, 92, SAFE_AREA.top + 18);
    rails.lineBetween(GAME_WIDTH - 92, SAFE_AREA.top + 18, GAME_WIDTH - 22, SAFE_AREA.top + 18);
  }

  private createBrandLockup(): void {
    const lockup = this.add.container(GAME_WIDTH / 2, 352);

    const signal = this.add.text(0, -88, 'FRONTLINE UPLINK // ACTIVE', {
      fontFamily: FONTS.numbers,
      fontSize: '11px',
      color: '#73F7FF',
      letterSpacing: 2,
    }).setOrigin(0.5);

    const claw = this.add.text(0, -42, 'CLAW', {
      fontFamily: FONTS.display,
      fontSize: '52px',
      color: '#F1FAFF',
      fontStyle: 'bold',
      letterSpacing: 7,
      stroke: '#021018',
      strokeThickness: 7,
      shadow: { offsetX: 0, offsetY: 8, color: '#00000099', blur: 0, fill: true },
    }).setOrigin(0.5);

    const shooter = this.add.text(0, 14, 'SHOOTER', {
      fontFamily: FONTS.display,
      fontSize: '47px',
      color: '#39FF14',
      fontStyle: 'bold',
      letterSpacing: 3,
      stroke: '#061A0C',
      strokeThickness: 7,
      shadow: { offsetX: 0, offsetY: 8, color: '#000000AA', blur: 0, fill: true },
    }).setOrigin(0.5);

    const rule = this.add.graphics();
    rule.lineStyle(2, 0x39FF14, 0.8);
    rule.lineBetween(-112, 61, -32, 61);
    rule.lineBetween(32, 61, 112, 61);
    rule.fillStyle(0x00D4FF, 1);
    rule.fillRect(-18, 59, 36, 4);

    const subtitle = this.add.text(0, 83, 'BUILD THE SQUAD. BREAK THE SWARM.', {
      fontFamily: FONTS.body,
      fontSize: '14px',
      color: COLOR_CSS.uiText,
      fontStyle: 'bold',
      letterSpacing: 1,
    }).setOrigin(0.5);

    lockup.add([signal, claw, shooter, rule, subtitle]);
    lockup.setAlpha(0).setY(365);
    this.tweens.add({
      targets: lockup,
      alpha: 1,
      y: 352,
      duration: 540,
      ease: 'Cubic.easeOut',
    });

    this.tweens.add({
      targets: signal,
      alpha: { from: 1, to: 0.45 },
      duration: 1050,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private createActions(): void {
    this.playButton = new Button(this, {
      x: GAME_WIDTH / 2,
      y: 558,
      text: 'Deploy',
      variant: 'primary',
      width: 244,
      height: 62,
      onClick: () => this.onPlayClick(),
    });

    const hint = this.add.text(GAME_WIDTH / 2, 607, 'SWIPE TO STEER  •  AUTO-FIRE ENABLED', {
      fontFamily: FONTS.numbers,
      fontSize: '11px',
      color: '#8BA9BC',
      letterSpacing: 1,
    }).setOrigin(0.5);

    new Button(this, {
      x: GAME_WIDTH / 2,
      y: GAME_HEIGHT - SAFE_AREA.bottom - 36,
      text: 'Settings',
      variant: 'secondary',
      width: 154,
      height: 46,
      onClick: () => this.goToSettings(),
    });

    this.tweens.add({
      targets: [this.playButton, hint],
      alpha: { from: 0.88, to: 1 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private onPlayClick(): void {
    this.transitionTo('LevelSelectScene');
  }

  private goToSettings(): void {
    this.transitionTo('SettingsScene');
  }

  private transitionTo(sceneKey: string): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(sceneKey);
    });
  }
}
